import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export type CardSpecValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | string[]
  | Record<string, any>;

interface ICardSpecs {
  [key: string]: CardSpecValue;
}

export interface IProduct {
  id: string;
  name: string;
  sku?: string;
  price?: number | string | null;
  image: string;
  description: string;
  seoTitle?: string;
  seoDescription?: string;
  categoryId: string;
  sort_order?: number | null;
  card_specs: ICardSpecs;
  mainImages?: string[]; // Дополнительные изображения для вариантов цвета
  variants?: IProductVariantSummary[];
  weight_grams?: number | string | null;
  package_length_cm?: number | string | null;
  package_width_cm?: number | string | null;
  package_height_cm?: number | string | null;
}

export interface IProductVariantSummary {
  id: string;
  name: string;
  sizes: string[];
  image?: string | null;
  images?: string[];
  sizeConfigs?: IProductVariantSizeConfig[];
}

export interface IProductVariantPriceTier {
  minQuantity: number | string | null;
  price: number | string | null;
}

export interface IProductVariantSizeConfig {
  id?: string;
  size: string;
  inStock?: boolean;
  stock?: number | string | null;
  sku?: string;
  priceTiers?: IProductVariantPriceTier[];
  weight_grams?: number | string | null;
  package_length_cm?: number | string | null;
  package_width_cm?: number | string | null;
  package_height_cm?: number | string | null;
}

export interface IProductVariant extends IProductVariantSummary {
  images: string[];
}

export type IContentBlock = ITextBlock | ITableBlock | IListBlock | IImageGalleryBlock | IFileBlock | ITitle | ICombinedBlock | IDivider;

export interface ICombinedBlock {
  type: 'combined';
  content: Array<(ITextBlock | ITableBlock | IListBlock | IImageGalleryBlock | IFileBlock | ITitle | IDivider)>;
}

export interface IDivider {
  type: 'divider';
}

export interface ITextBlock {
  type: 'text';
  title?: string;
  content: string;
}

export interface ITitle {
  type: 'title';
  text: string;
  level: number;
}

export interface ITableBlock {
  type: 'table';
  title?: string;
  headers: (string | ITableCellData)[];
  rows: Array<(string | ITableCellData)[]>;
}

export interface ITableCellData {
  content: string;
  colspan?: number;
  rowspan?: number;
  align?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
}

export interface IListBlock {
  type: 'list';
  title?: string;
  items: string[];
}

export interface IImageGalleryBlock {
  type: 'imageGallery';
  images: string[];
}

export interface IFileBlock {
  type: 'file';
  title?: string;
  files: {
    name: string;
    url: string;
  }[];
}

export interface IProductDetails {
  id: string;
  name: string;
  mainImages: string[];
  variants: IProductVariant[];
  contentBlocks: IContentBlock[];
}

interface IProductState {
  byCategory: {
    [categoryId: string]: {
      products: IProduct[];
      loading: boolean;
      error: string | null;
    };
  };
  productDetails: {
    [productId: string]: {
      details: IProductDetails | null;
      loading: boolean;
      error: string | null;
    };
  };
  productInfo: {
    [productId: string]: {
      product: IProduct | null;
      loading: boolean;
      error: string | null;
    };
  };
}

export const ROOT_CATEGORY_KEY = '__root__';
export const ALL_PRODUCTS_KEY = '__all__';

const initialState: IProductState = {
  byCategory: {},
  productDetails: {},
  productInfo: {},
};

export const fetchProductInfo = createAsyncThunk(
  'products/fetchProductInfo',
  async (productId: string) => {
    try {
      const { data } = await api.get(`/api/products/${productId}`);
      return { productId, product: data as IProduct };
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Продукт не найден');
      }
      throw new Error('Ошибка при загрузке информации о продукте');
    }
  }
);

export const fetchProductsByCategory = createAsyncThunk<
  { categoryId: string; products: IProduct[] },
  string,
  { state: { products: IProductState } }
>(
  'products/fetchByCategory',
  async (categoryId: string) => {
    try {
      const { data } = await api.get(`/api/categories/${categoryId}/products`);
      return { categoryId, products: data as IProduct[] };
    } catch (error: any) {
      if (error.response?.status === 404) {
        return { categoryId, products: [] };
      }
      throw new Error('Ошибка при загрузке товаров');
    }
  },
  {
    condition: (categoryId, { getState }) => {
      const category = getState().products.byCategory[categoryId];
      if (!category) {
        return true;
      }
      if (category.loading || category.products.length > 0) {
        return false;
      }
      return true;
    },
  }
);

export const fetchCategoryLandingProducts = createAsyncThunk<
  { categoryId: string; products: IProduct[] },
  string,
  { state: { products: IProductState } }
>(
  'products/fetchCategoryLanding',
  async (categoryId: string) => {
    try {
      const { data } = await api.get(
        `/api/categories/${categoryId}/products/?include_descendants=1&commercial_ready=1`
      );
      return { categoryId, products: data as IProduct[] };
    } catch (error: any) {
      if (error.response?.status === 404) {
        return { categoryId, products: [] };
      }
      throw new Error('Ошибка при загрузке товаров категории');
    }
  },
  {
    condition: (categoryId, { getState }) => {
      const category = getState().products.byCategory[categoryId];
      return !category || (!category.loading && category.products.length === 0);
    },
  }
);
export const fetchRootProducts = createAsyncThunk<{ categoryId: string; products: IProduct[] }, void, { state: { products: IProductState } }>(
  'products/fetchRootProducts',
  async () => {
    try {
      const { data } = await api.get('/api/categories/root-products/');
      return { categoryId: ROOT_CATEGORY_KEY, products: data as IProduct[] };
    } catch (error: any) {
      throw new Error('Не удалось загрузить товары из каталога');
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as { products: IProductState };
      const rootCategory = state.products.byCategory[ROOT_CATEGORY_KEY];
      return !rootCategory || (!rootCategory.loading && rootCategory.products.length === 0);
    },
  }
);

export const fetchAllProducts = createAsyncThunk<{ categoryId: string; products: IProduct[] }, void, { state: { products: IProductState } }>(
  'products/fetchAllProducts',
  async () => {
    try {
      const { data } = await api.get('/api/products/');
      return { categoryId: ALL_PRODUCTS_KEY, products: data as IProduct[] };
    } catch (error: any) {
      throw new Error('Не удалось загрузить товары из каталога');
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as { products: IProductState };
      const allCategory = state.products.byCategory[ALL_PRODUCTS_KEY];
      return !allCategory || (!allCategory.loading && allCategory.products.length === 0);
    },
  }
);

export const fetchProductDetails = createAsyncThunk(
  'products/fetchProductDetails',
  async (productId: string) => {
    try {
      const { data } = await api.get(`/api/products/${productId}/details`);
      return { productId, details: data as IProductDetails };
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Продукт не найден');
      }
      throw new Error('Ошибка при загрузке деталей продукта');
    }
  }
);

const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Обработка fetchProductsByCategory
    builder
      .addCase(fetchProductsByCategory.pending, (state, action) => {
        const categoryId = action.meta.arg;
        if (!state.byCategory[categoryId]) {
          state.byCategory[categoryId] = {
            products: [],
            loading: true,
            error: null,
          };
        } else {
          state.byCategory[categoryId].loading = true;
          state.byCategory[categoryId].error = null;
        }
      })
      .addCase(fetchProductsByCategory.fulfilled, (state, action) => {
        const { categoryId, products } = action.payload;
        state.byCategory[categoryId] = {
          products,
          loading: false,
          error: null,
        };
      })
      .addCase(fetchProductsByCategory.rejected, (state, action) => {
        const categoryId = action.meta.arg;
        if (!state.byCategory[categoryId]) {
          state.byCategory[categoryId] = {
            products: [],
            loading: false,
            error: action.error.message || 'Ошибка',
          };
        } else {
          state.byCategory[categoryId].loading = false;
          state.byCategory[categoryId].error = action.error.message || 'Ошибка';
        }
      });

    builder
      .addCase(fetchCategoryLandingProducts.pending, (state, action) => {
        const categoryId = action.meta.arg;
        state.byCategory[categoryId] = {
          products: state.byCategory[categoryId]?.products || [],
          loading: true,
          error: null,
        };
      })
      .addCase(fetchCategoryLandingProducts.fulfilled, (state, action) => {
        const { categoryId, products } = action.payload;
        state.byCategory[categoryId] = { products, loading: false, error: null };
      })
      .addCase(fetchCategoryLandingProducts.rejected, (state, action) => {
        const categoryId = action.meta.arg;
        state.byCategory[categoryId] = {
          products: state.byCategory[categoryId]?.products || [],
          loading: false,
          error: action.error.message || 'Ошибка',
        };
      });
    // Обработка fetchProductDetails
    builder
      .addCase(fetchRootProducts.pending, (state) => {
        if (!state.byCategory[ROOT_CATEGORY_KEY]) {
          state.byCategory[ROOT_CATEGORY_KEY] = {
            products: [],
            loading: true,
            error: null,
          };
        } else {
          state.byCategory[ROOT_CATEGORY_KEY].loading = true;
          state.byCategory[ROOT_CATEGORY_KEY].error = null;
        }
      })
      .addCase(fetchRootProducts.fulfilled, (state, action) => {
        const { categoryId, products } = action.payload;
        state.byCategory[categoryId] = {
          products,
          loading: false,
          error: null,
        };
      })
      .addCase(fetchRootProducts.rejected, (state, action) => {
        if (!state.byCategory[ROOT_CATEGORY_KEY]) {
          state.byCategory[ROOT_CATEGORY_KEY] = {
            products: [],
            loading: false,
            error: action.error.message || 'Ошибка',
          };
        } else {
          state.byCategory[ROOT_CATEGORY_KEY].loading = false;
          state.byCategory[ROOT_CATEGORY_KEY].error = action.error.message || 'Ошибка';
        }
      });
    builder
      .addCase(fetchAllProducts.pending, (state) => {
        if (!state.byCategory[ALL_PRODUCTS_KEY]) {
          state.byCategory[ALL_PRODUCTS_KEY] = {
            products: [],
            loading: true,
            error: null,
          };
        } else {
          state.byCategory[ALL_PRODUCTS_KEY].loading = true;
          state.byCategory[ALL_PRODUCTS_KEY].error = null;
        }
      })
      .addCase(fetchAllProducts.fulfilled, (state, action) => {
        const { categoryId, products } = action.payload;
        state.byCategory[categoryId] = {
          products,
          loading: false,
          error: null,
        };
      })
      .addCase(fetchAllProducts.rejected, (state, action) => {
        if (!state.byCategory[ALL_PRODUCTS_KEY]) {
          state.byCategory[ALL_PRODUCTS_KEY] = {
            products: [],
            loading: false,
            error: action.error.message || 'Ошибка',
          };
        } else {
          state.byCategory[ALL_PRODUCTS_KEY].loading = false;
          state.byCategory[ALL_PRODUCTS_KEY].error = action.error.message || 'Ошибка';
        }
      });
    builder
      .addCase(fetchProductDetails.pending, (state, action) => {
        const productId = action.meta.arg;
        if (!state.productDetails[productId]) {
          state.productDetails[productId] = {
            details: null,
            loading: true,
            error: null,
          };
        } else {
          state.productDetails[productId].loading = true;
          state.productDetails[productId].error = null;
        }
      })
      .addCase(fetchProductDetails.fulfilled, (state, action) => {
        const { productId, details } = action.payload;
        state.productDetails[productId] = {
          details,
          loading: false,
          error: null,
        };
      })
      .addCase(fetchProductDetails.rejected, (state, action) => {
        const productId = action.meta.arg;
        if (!state.productDetails[productId]) {
          state.productDetails[productId] = {
            details: null,
            loading: false,
            error: action.error.message || 'Ошибка',
          };
        } else {
          state.productDetails[productId].loading = false;
          state.productDetails[productId].error = action.error.message || 'Ошибка';
        }
      });
    builder
      .addCase(fetchProductInfo.pending, (state, action) => {
        const productId = action.meta.arg;
        if (!state.productInfo[productId]) {
          state.productInfo[productId] = {
            product: null,
            loading: true,
            error: null,
          };
        } else {
          state.productInfo[productId].loading = true;
          state.productInfo[productId].error = null;
        }
      })
      .addCase(fetchProductInfo.fulfilled, (state, action) => {
        const { productId, product } = action.payload;
        state.productInfo[productId] = {
          product,
          loading: false,
          error: null,
        };
      })
      .addCase(fetchProductInfo.rejected, (state, action) => {
        const productId = action.meta.arg;
        if (!state.productInfo[productId]) {
          state.productInfo[productId] = {
            product: null,
            loading: false,
            error: action.error.message || 'Ошибка при загрузке информации о продукте',
          };
        } else {
          state.productInfo[productId].loading = false;
          state.productInfo[productId].error = action.error.message || 'Ошибка при загрузке информации о продукте';
        }
      });
  },
});

export default productSlice.reducer;
